import { supabase } from '../lib/supabase';

/**
 * Service for managing event check-in operations
 */
export const checkInService = {
  /**
   * Get all registrations for an event (for organizers)
   * @param {string} eventId - Event UUID
   * @returns {Promise<Array>} List of registrations with attendee details
   */
  async getEventRegistrations(eventId) {
    const { data, error } = await supabase?.from('event_registrations')?.select(`
        id,
        participant_name,
        participant_email,
        participant_phone,
        equipment_rental,
        special_requirements,
        status,
        checked_in,
        checked_in_at,
        created_at,
        user_id,
        events!inner(
          title,
          event_date,
          event_time
        )
      `)?.eq('event_id', eventId)?.eq('status', 'approved')?.order('participant_name');

    if (error) throw error;

    return data?.map(reg => ({
      id: reg?.id,
      participantName: reg?.participant_name,
      participantEmail: reg?.participant_email,
      participantPhone: reg?.participant_phone,
      equipmentRental: reg?.equipment_rental,
      specialRequirements: reg?.special_requirements,
      status: reg?.status,
      checkedIn: reg?.checked_in,
      checkedInAt: reg?.checked_in_at,
      createdAt: reg?.created_at,
      userId: reg?.user_id,
      eventTitle: reg?.events?.title,
      eventDate: reg?.events?.event_date,
      eventTime: reg?.events?.event_time
    }));
  },

  /**
   * Perform check-in via QR code scan
   * @param {string} registrationId - Registration UUID from QR code
   * @returns {Promise<Object>} Check-in result with attendee details
   */
  async checkInByQR(registrationId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.rpc('check_in_attendee', {
      registration_uuid: registrationId,
      organizer_uuid: user?.id
    });

    if (error) throw error;
    return data;
  },

  /**
   * Manual check-in by searching attendee
   * @param {string} eventId - Event UUID
   * @param {string} searchTerm - Name or email to search
   * @returns {Promise<Array>} Matching registrations
   */
  async searchAttendees(eventId, searchTerm) {
    const { data, error } = await supabase?.from('event_registrations')?.select(`
        id,
        participant_name,
        participant_email,
        participant_phone,
        equipment_rental,
        special_requirements,
        checked_in,
        checked_in_at
      `)?.eq('event_id', eventId)?.eq('status', 'approved')?.or(`participant_name.ilike.%${searchTerm}%,participant_email.ilike.%${searchTerm}%`);

    if (error) throw error;

    return data?.map(reg => ({
      id: reg?.id,
      participantName: reg?.participant_name,
      participantEmail: reg?.participant_email,
      participantPhone: reg?.participant_phone,
      equipmentRental: reg?.equipment_rental,
      specialRequirements: reg?.special_requirements,
      checkedIn: reg?.checked_in,
      checkedInAt: reg?.checked_in_at
    }));
  },

  /**
   * Get check-in statistics for an event
   * @param {string} eventId - Event UUID
   * @returns {Promise<Object>} Statistics object
   */
  async getCheckInStats(eventId) {
    const { data, error } = await supabase?.from('event_registrations')?.select('checked_in, status')?.eq('event_id', eventId)?.eq('status', 'approved');

    if (error) throw error;

    const total = data?.length;
    const checkedIn = data?.filter(r => r?.checked_in)?.length;
    const pending = total - checkedIn;

    return {
      total,
      checkedIn,
      pending,
      percentage: total > 0 ? Math.round((checkedIn / total) * 100) : 0
    };
  },

  /**
   * Subscribe to real-time check-in updates
   * @param {string} eventId - Event UUID
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription object
   */
  subscribeToCheckIns(eventId, callback) {
    const channel = supabase?.channel(`checkins:${eventId}`)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'event_registrations',
          filter: `event_id=eq.${eventId}`
        },
        (payload) => {
          callback({
            id: payload?.new?.id,
            checkedIn: payload?.new?.checked_in,
            checkedInAt: payload?.new?.checked_in_at,
            participantName: payload?.new?.participant_name
          });
        }
      )?.subscribe();

    return channel;
  }
};